import { getSafePropertyChain } from '@empathy/get-safe-property-chain';
import { Result } from '@empathy/search-types';
import { StorageService } from '@empathy/storage-service';
import { container } from '../../../container';
import { DEPENDENCIES, STORAGE } from '../../../container/dependencies.const';
import { EmpathyXConfig } from '../../../models';
import { ClickedResultHistory } from '../../../models';
import { UserService } from '../../../services';
import { GETTERS_KEYS } from '../../store.const';
import { CLICKED_RESULT_HISTORY_KEY, HISTORY_ACTIONS_KEYS, HISTORY_MUTATIONS_KEYS } from '../history.const';
import { HistoryAction, HistoryContext } from '../history.interfaces';

export class ClickedResultHistoryAction implements HistoryAction {
  protected userService: UserService;
  protected storageService: StorageService;
  protected context: HistoryContext;

  actions = {
    [HISTORY_ACTIONS_KEYS.addResultIdToClicksHistory]: (context: HistoryContext, result: Result): void => {
      this.initDependencies(context);
      const currentUserId = this.getCurrentUserIdAndRemoveHistoryIfNecessary();
      if (currentUserId) {
        const newClickedResultHistory = this.generateNewHistory(result, currentUserId);
        this.storageService.setItem(CLICKED_RESULT_HISTORY_KEY, newClickedResultHistory);
        context.commit(HISTORY_MUTATIONS_KEYS.setClickedResultHistory, newClickedResultHistory);
      }
    },

    [HISTORY_ACTIONS_KEYS.loadClickedResultHistory]: (context: HistoryContext): void => {
      this.initDependencies(context);
      const currentUserId = this.getCurrentUserIdAndRemoveHistoryIfNecessary();
      if (currentUserId) {
        const history = this.storageService.getItem<string[]>(CLICKED_RESULT_HISTORY_KEY);
        context.commit(HISTORY_MUTATIONS_KEYS.setClickedResultHistory, history || { history: [] });
      }
    }
  };

  protected generateNewHistory(result: Result, currentUserId: string): ClickedResultHistory {
    const { history: config }: EmpathyXConfig = this.context.rootGetters[GETTERS_KEYS.config.config];
    const newResultId = getSafePropertyChain(result, config.clickedResultIdFieldForHistory);
    const storedClickedResults = this.context.state.clickedResultHistory;
    const newHistory = [newResultId, ...storedClickedResults.history.filter(historyItem => newResultId !== historyItem)];
    newHistory.splice(config.clickedResultHistorySize);
    return {
      history: newHistory,
      userId: currentUserId
    };
  }

  protected initDependencies(context: HistoryContext) {
    this.context = context;
    if (!this.userService) {
      this.userService = container.get<UserService>(DEPENDENCIES.UserService);
    }
    if (!this.storageService) {
      this.storageService = container.getNamed<StorageService>(DEPENDENCIES.StorageService, STORAGE.Local);
    }
  }

  protected getCurrentUserIdAndRemoveHistoryIfNecessary(): string | null {
    let currentUserId = null;
    if (this.context.rootState.config.dynamic.consent) {
      const userInfo = this.userService.getUserInfo();
      currentUserId = userInfo ? userInfo.user : null;
    }
    this.removeHistoryIfUserIdDoesntMatch(currentUserId);
    return currentUserId;
  }

  protected removeHistoryIfUserIdDoesntMatch(currentUserId: string | null) {
    const clickedResultHistory = this.storageService.getItem<ClickedResultHistory>(CLICKED_RESULT_HISTORY_KEY);
    if (clickedResultHistory && currentUserId !== clickedResultHistory.userId) {
      this.storageService.removeItem(CLICKED_RESULT_HISTORY_KEY);
      this.context.commit(HISTORY_MUTATIONS_KEYS.setClickedResultHistory, { history: [] });
    }
  }
}
